import React, { useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import CommonModal from '../Common/CommonModal';
import { useGetRingCentralAccountsQuery } from '@/app/api/ringCentralApiSlice';
import { RingCentralClient } from 'app/shared-components/Types/RingCentralClient';
import { useTheme } from '@mui/material/styles';

interface RingCentralAccountModalProps {
  userId: string;
  onAccountSelect: (clientId: string, userId: string) => void;
}

const RingCentralAccountModal: React.FC<RingCentralAccountModalProps> = ({
  userId,
  onAccountSelect,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { data: accounts, isLoading, error } = useGetRingCentralAccountsQuery();

  useEffect(() => {
    if (!isLoading && accounts && accounts.length === 1) {
      onAccountSelect(accounts[0].clientId, userId);
      dispatch(closeDialog());
    }
  }, [isLoading, accounts, userId, onAccountSelect, dispatch]);

  const handleItemClick = (item: RingCentralClient) => {
    onAccountSelect(item.clientId, userId);
    dispatch(closeDialog());
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box display='flex' justifyContent='center' p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={3}>
          <Alert severity='error'>
            Failed to load available RingCentral accounts. Please try again.
          </Alert>
        </Box>
      );
    }

    if (!accounts || accounts.length === 0) {
      return (
        <Box p={3}>
          <Alert severity='info'>No RingCentral accounts available.</Alert>
        </Box>
      );
    }

    return (
      <List sx={{ pt: 0 }}>
        {accounts.map((account) => (
          <ListItem key={account.clientId} disablePadding>
            <ListItemButton
              onClick={() => handleItemClick(account)}
              sx={{
                borderRadius: 1,
                margin: '4px 8px',
              }}
            >
              <ListItemText
                primary={
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 400, color: theme.palette.text.primary }}
                  >
                    {account.displayName}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Client ID: {account.clientId}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <CommonModal title='Please select Ring Central account'>
      <Box sx={{ minWidth: 400, maxHeight: 400, overflow: 'auto' }}>{renderContent()}</Box>
    </CommonModal>
  );
};

export default RingCentralAccountModal;
